import { isNonEmptyString, isStringArray } from '@/api/queries/utils';
import { FilterLimitPresets } from '@/models/filter/filterDefaultLimits';
import expandHierarchicalPaths from '@/models/filter/utils/expandHierarchicalPaths';
import { getDeepestHierarchyLevel, HIERARCHY_SEPARATOR } from '@/models/filter/utils/hierarchyPath';
import type { GetOptionsQueryFunction } from '@/types/cube/query';
import type { FilterConfigDynamic } from '@/types/filter/filterConfig';
import type { SelectItem } from '@/types/ui/selectItem';
import type { CubeRawRow } from '@/utils/cube/mapper.type';
import { toStringOrNull } from '@/utils/cube/mapper';
import type { Filter } from '@cubejs-client/core';

export const HCP_ASSOCIATION_CUBE_NAME = 'hcp_association';

const PARENT_ASSOCIATION_DIMENSION = `${HCP_ASSOCIATION_CUBE_NAME}.parent_association_name`;
const ASSOCIATION_DIMENSION = `${HCP_ASSOCIATION_CUBE_NAME}.association_name`;
const PAST_POSITION_DIMENSION = `${HCP_ASSOCIATION_CUBE_NAME}.association_past_position`;

const currentMembershipFilter: Filter = {
  member: PAST_POSITION_DIMENSION,
  operator: 'equals',
  values: ['false'],
};

export const getScientificOrganizationOptions = (): ReturnType<GetOptionsQueryFunction> => ({
  dimensions: [PARENT_ASSOCIATION_DIMENSION, ASSOCIATION_DIMENSION],
  filters: [currentMembershipFilter],
  limit: FilterLimitPresets.LIMIT_50K,
});

export const mapScientificOrganizationOption = (row: CubeRawRow): SelectItem<string> | null => {
  const parentAssociationName = toStringOrNull(row[PARENT_ASSOCIATION_DIMENSION]);
  const associationName = toStringOrNull(row[ASSOCIATION_DIMENSION]);

  if (!parentAssociationName) return null;

  const value = associationName
    ? `${parentAssociationName}${HIERARCHY_SEPARATOR}${associationName}`
    : parentAssociationName;

  return { label: value, value };
};

const unsetChildAssociationFilter: Filter = {
  or: [
    { member: ASSOCIATION_DIMENSION, operator: 'equals', values: ['N/A'] },
    { member: ASSOCIATION_DIMENSION, operator: 'notSet' },
  ],
};

let cachedScientificOrganizationPaths: string[] = [];

export const resetScientificOrganizationPathsCache = (): void => {
  cachedScientificOrganizationPaths = [];
};

const cacheScientificOrganizationPaths = (options: SelectItem<string>[]): SelectItem<string>[] => {
  const processed = expandHierarchicalPaths(options);
  cachedScientificOrganizationPaths = processed.map(option => option.value);
  return processed;
};

const getHierarchyParentPath = (selectedPath: string): string | undefined => {
  const separatorIndex = selectedPath.lastIndexOf(HIERARCHY_SEPARATOR);
  return separatorIndex > 0 ? selectedPath.slice(0, separatorIndex) : undefined;
};

const groupDirectChildPathsByParent = (allPaths: readonly string[]): ReadonlyMap<string, string[]> => {
  const directChildPathsByParent = new Map<string, string[]>();

  allPaths.forEach(optionPath => {
    const parentPath = getHierarchyParentPath(optionPath);
    if (!parentPath) return;

    const directChildPaths = directChildPathsByParent.get(parentPath);
    if (directChildPaths) {
      directChildPaths.push(optionPath);
      return;
    }

    directChildPathsByParent.set(parentPath, [optionPath]);
  });

  return directChildPathsByParent;
};

// The expanded option set holds every hierarchy level (up to LIMIT_50K rows) and
// applyFilter runs once per useGetGlobalFilters consumer, so the grouping is kept
// per option-set identity instead of being rebuilt on every call.
const directChildPathsByParentCache = new WeakMap<readonly string[], ReadonlyMap<string, string[]>>();

const getDirectChildPathsByParent = (allPaths: readonly string[]): ReadonlyMap<string, string[]> => {
  const cachedGrouping = directChildPathsByParentCache.get(allPaths);
  if (cachedGrouping) return cachedGrouping;

  const grouping = groupDirectChildPathsByParent(allPaths);
  directChildPathsByParentCache.set(allPaths, grouping);
  return grouping;
};

const buildParentEqualsFilter = (parentName: string): Filter => ({
  member: PARENT_ASSOCIATION_DIMENSION,
  operator: 'equals',
  values: [parentName],
});

const buildParentScopeFilter = (parentName: string, selectedChildNames: string[]): Filter => ({
  and: [
    buildParentEqualsFilter(parentName),
    {
      or: [
        { member: ASSOCIATION_DIMENSION, operator: 'equals', values: selectedChildNames },
        unsetChildAssociationFilter,
      ],
    },
  ],
});

const buildChildrenScopeFilter = (parentName: string, selectedChildNames: string[]): Filter => ({
  and: [
    buildParentEqualsFilter(parentName),
    { member: ASSOCIATION_DIMENSION, operator: 'equals', values: selectedChildNames },
  ],
});

const getSelectedChildPaths = (
  selectedPaths: ReadonlySet<string>,
  knownDirectChildPaths: readonly string[],
  selectedDirectChildPaths: readonly string[],
): string[] => {
  if (knownDirectChildPaths.length) {
    return knownDirectChildPaths.filter(childPath => selectedPaths.has(childPath));
  }

  return [...selectedDirectChildPaths];
};

export const buildScientificOrganizationFilter = (
  value: unknown,
  allPaths: readonly string[] = cachedScientificOrganizationPaths,
): Filter | undefined => {
  if (!isStringArray(value) || !value.length) return undefined;

  const selectedPaths = new Set(value);
  const consumedPaths = new Set<string>();
  const selectedOrganizationFilters: Filter[] = [];
  const directChildPathsByParent = getDirectChildPathsByParent(allPaths);
  const selectedChildPathsByParent = groupDirectChildPathsByParent(value);

  value.forEach(selectedPath => {
    if (!selectedPath || consumedPaths.has(selectedPath) || getHierarchyParentPath(selectedPath)) return;

    const knownDirectChildPaths = directChildPathsByParent.get(selectedPath) ?? [];
    const selectedDirectChildPaths = selectedChildPathsByParent.get(selectedPath) ?? [];
    const selectedChildPaths = getSelectedChildPaths(selectedPaths, knownDirectChildPaths, selectedDirectChildPaths);
    consumedPaths.add(selectedPath);
    selectedChildPaths.forEach(childPath => consumedPaths.add(childPath));

    const includesEveryRelatedChild =
      selectedChildPaths.length === 0 || selectedChildPaths.length === knownDirectChildPaths.length;

    if (includesEveryRelatedChild) {
      selectedOrganizationFilters.push(buildParentEqualsFilter(selectedPath));
      return;
    }

    selectedOrganizationFilters.push(
      buildParentScopeFilter(selectedPath, selectedChildPaths.map(getDeepestHierarchyLevel)),
    );
  });

  const remainingChildNamesByParent = new Map<string, string[]>();
  value.forEach(selectedPath => {
    if (!selectedPath || consumedPaths.has(selectedPath)) return;

    const parentPath = getHierarchyParentPath(selectedPath);
    if (!parentPath) return;

    const childNames = remainingChildNamesByParent.get(parentPath);
    if (childNames) {
      childNames.push(getDeepestHierarchyLevel(selectedPath));
      return;
    }

    remainingChildNamesByParent.set(parentPath, [getDeepestHierarchyLevel(selectedPath)]);
  });

  remainingChildNamesByParent.forEach((selectedChildNames, parentName) => {
    selectedOrganizationFilters.push(buildChildrenScopeFilter(parentName, selectedChildNames));
  });

  if (!selectedOrganizationFilters.length) return undefined;

  return {
    and: [{ or: selectedOrganizationFilters }, currentMembershipFilter],
  };
};

const scientificOrganizationFilter: FilterConfigDynamic = {
  label: 'global.filters.formControls.scientificOrganizationLabel',
  placeholder: 'global.filters.formControls.scientificOrganizationPlaceholder',
  sourceCube: HCP_ASSOCIATION_CUBE_NAME,
  defaultValue: [],
  getOptionsQuery: getScientificOrganizationOptions,
  mapResult: mapScientificOrganizationOption,
  processOptions: cacheScientificOrganizationPaths,
  type: 'treeselect',
  selectionMode: 'indication',
  searchable: true,
  clearable: true,
  applyDynamicFilters: true,
  format: ({ value }) => (isNonEmptyString(value) ? getDeepestHierarchyLevel(value) : ''),
  applyFilter: value => buildScientificOrganizationFilter(value),
};

export default scientificOrganizationFilter;
